import moment from 'moment';
import memo from 'memoize-one';
import { funcState } from '../../utils/funcState';
import { Filters } from '../utils/filtersEnum';
import { asArray } from '../utils/asArray';
import { toDateRangeObjects, toDateRangeStrings } from '../utils/date';

const getAvailableDates = () => {
  const getStartDate = (date) =>
    date.isBefore(moment.utc()) ? moment.utc() : date;

  function getLabel(futureDate) {
    const currentYear = moment().format('YYYY');
    const futureDateYear = futureDate.format('YYYY');
    if (futureDateYear !== currentYear) {
      return futureDate.format('MMMM YYYY');
    }

    return futureDate.format('MMMM');
  }

  return [...Array(8).keys()].map((index) => {
    const month = moment.utc().add(index, 'months');
    return {
      id: index + 1,
      label: getLabel(month),
      minDate: getStartDate(month.startOf('month')).toISOString(),
      maxDate: month.endOf('month').toISOString(),
    };
  });
};

const getAvailableDatesMemo = memo(getAvailableDates);

const DateFilterProvider = ({ updateFilter }) => {
  const [availableDates, setAvailableDates] = funcState([]);
  const [selectedDateIds, setSelectedDateIds] = funcState([]);
  const [dateRange, setDateRange] = funcState();

  const getDateRanges = () => {
    if (dateRange.value?.min && dateRange.value?.max) {
      return [{ min: dateRange.value.min, max: dateRange.value.max }];
    }

    const dates = selectedDateIds.value.map((date) =>
      availableDates.value.find((n) => n.id === date),
    );

    const dateRanges = dates.map((n) => ({ min: n.minDate, max: n.maxDate }));

    return dateRanges;
  };

  // TODO:(tomo@) Change the backend to send a better API
  //              to be used for dates. Remove the querystring
  //              prop. To be done after Discovery experiment
  //              has gone 100%.
  const update = ({ queryParams }) => {
    const selectedPopularDateIds = queryParams?.selectedPopularDateIds;

    setAvailableDates(getAvailableDatesMemo());

    if (selectedPopularDateIds || selectedPopularDateIds?.length) {
      setDateRange(undefined);
      setSelectedDateIds(
        asArray(selectedPopularDateIds).map((n) => parseInt(n, 10)),
      );
    } else if (queryParams?.dateRanges) {
      setSelectedDateIds([]);
      const paramDateRanges = Array.isArray(queryParams.dateRanges)
        ? queryParams.dateRanges
        : [queryParams.dateRanges];
      const dateRangeObj = toDateRangeObjects(paramDateRanges)[0];
      setDateRange(dateRangeObj);
    } else {
      setDateRange(undefined);
      setSelectedDateIds([]);
    }

    updateFilter({
      key: Filters.Date,
      value: {
        availableDates: availableDates.value,
        selectedDateIds: selectedDateIds.value,
        dateRanges: getDateRanges(),
      },
    });
  };

  const setDate = ({ selectedPopularDateIds }) => {
    setSelectedDateIds(asArray(selectedPopularDateIds));

    updateFilter({
      key: Filters.Date,
      value: {
        availableDates: availableDates.value,
        selectedDateIds: selectedDateIds.value,
        dateRanges: getDateRanges(),
      },
    });
  };

  const toQueryParams = () => {
    return {
      dateRanges: toDateRangeStrings(getDateRanges()),
      selectedPopularDateIds: selectedDateIds.value,
    };
  };

  const defaultFilters = {
    dateRanges: undefined,
    selectedPopularDateIds: undefined,
  };

  const hasActiveFilters = () => {
    return !!selectedDateIds.value.length;
  };

  return {
    exposed: { setDate },
    update,
    toQueryParams,
    hasActiveFilters,
    defaultFilters,
  };
};

export default DateFilterProvider;
